<!-- Trigger — icon / button / tile, per triggerMode. -->
<div class="dvm-popup-trigger-shell" [class.is-icon]="triggerMode === 'icon'">
  <button
    *ngIf="triggerMode === 'icon'"
    type="button"
    class="dvm-popup-trigger dvm-popup-trigger-icon"
    [ngStyle]="triggerStyles"
    [title]="triggerHint || triggerLabel"
    [attr.aria-label]="triggerHint || triggerLabel"
    (click)="openPopup()"
  >
    <mat-icon>{{ triggerIcon }}</mat-icon>
  </button>

  <button
    *ngIf="triggerMode === 'button'"
    type="button"
    class="dvm-popup-trigger dvm-popup-trigger-button"
    [ngStyle]="triggerStyles"
    [title]="triggerHint"
    (click)="openPopup()"
  >
    <mat-icon *ngIf="triggerIcon">{{ triggerIcon }}</mat-icon>
    <span>{{ triggerLabel }}</span>
  </button>

  <button
    *ngIf="triggerMode === 'tile'"
    type="button"
    class="dvm-popup-trigger dvm-popup-trigger-tile"
    [ngStyle]="triggerStyles"
    [title]="triggerHint"
    (click)="openPopup()"
  >
    <mat-icon *ngIf="triggerIcon">{{ triggerIcon }}</mat-icon>
    <span class="dvm-popup-tile-label">{{ triggerLabel }}</span>
    <small *ngIf="triggerHint" class="dvm-popup-tile-hint">{{ triggerHint }}</small>
  </button>
</div>

<!-- Overlay dialog — fixed backdrop + dialog; center modal or right drawer. -->
<div *ngIf="open" #overlayRoot class="dvm-popup-backdrop" [class.is-drawer]="popupPosition === 'drawer'" (click)="onBackdropClick()">
  <div
    class="dvm-popup-dialog"
    [class.size-small]="popupSize === 'small'"
    [class.size-medium]="popupSize === 'medium'"
    [class.size-large]="popupSize === 'large'"
    [class.size-fullscreen]="popupSize === 'fullscreen'"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="title || triggerLabel"
    [ngStyle]="dialogStyles"
    [style.--dvm-popup-body-bg]="styleColor('bodyBg')"
    [style.--dvm-popup-body-text]="styleColor('bodyText')"
    [style.--dvm-popup-btn-bg]="styleColor('buttonBg')"
    [style.--dvm-popup-btn-text]="styleColor('buttonText')"
    [style.--dvm-popup-btn-radius]="stylePx('buttonRadius')"
    [style.--dvm-popup-radius]="stylePx('popupRadius')"
    (click)="$event.stopPropagation()"
  >
    <header class="dvm-popup-header" [ngStyle]="headerStyles">
      <h3 [style.color]="headerTextColor">{{ title || triggerLabel }}</h3>
      <button type="button" class="dvm-popup-close" aria-label="Close" [style.color]="headerTextColor" (click)="closePopup()">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="dvm-popup-body">
      <ng-container *ngFor="let block of blocks; let i = index; trackBy: trackByIndex">
        <ng-container [ngSwitch]="block.type">
          <!-- Heading -->
          <div *ngSwitchCase="'heading'" class="dvm-popup-block dvm-popup-heading" [style.text-align]="block.align || 'left'">
            <h2 *ngIf="headingTag(block) === 'h1'" class="hl-1">{{ block.text }}</h2>
            <h3 *ngIf="headingTag(block) === 'h2'" class="hl-2">{{ block.text }}</h3>
            <h4 *ngIf="headingTag(block) === 'h3'" class="hl-3">{{ block.text }}</h4>
          </div>

          <!-- Text paragraph(s) — newline-separated. -->
          <p *ngSwitchCase="'text'" class="dvm-popup-block dvm-popup-text" [style.text-align]="block.align || 'left'">{{ block.text }}</p>

          <!-- Key / value rows (audit metadata etc.). -->
          <dl *ngSwitchCase="'keyValue'" class="dvm-popup-block dvm-popup-kv">
            <ng-container *ngFor="let pair of block.pairs || []; trackBy: trackByIndex">
              <div class="dvm-popup-kv-row" *ngIf="pair.label || pair.value">
                <dt>{{ pair.label }}</dt>
                <dd>{{ pair.value }}</dd>
              </div>
            </ng-container>
          </dl>

          <!-- Data table (rows snapshotted at build time). -->
          <div *ngSwitchCase="'table'" class="dvm-popup-block dvm-popup-table-wrap">
            <table class="dvm-popup-table" *ngIf="tableRows(block).length; else emptyTable">
              <thead>
                <tr>
                  <th *ngFor="let col of tableColumns(block); trackBy: trackByIndex">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of tableRows(block); trackBy: trackByIndex">
                  <td *ngFor="let col of tableColumns(block); trackBy: trackByIndex">{{ formatCell(row[col]) }}</td>
                </tr>
              </tbody>
            </table>
            <ng-template #emptyTable>
              <div class="dvm-popup-table-empty">No data available.</div>
            </ng-template>
          </div>

          <!-- API-sourced table — fetched when the popup opens. Renders flat,
               or as collapsible per-group sections (change-history style) when
               groupsPath is configured. -->
          <div *ngSwitchCase="'apiTable'" class="dvm-popup-block dvm-popup-api">
            <div class="dvm-popup-api-loading" *ngIf="apiState[i]?.loading">
              <span class="dvm-popup-api-spinner" aria-hidden="true"></span> Loading data…
            </div>
            <div class="dvm-popup-api-error" *ngIf="apiState[i]?.error">{{ apiState[i]?.error }}</div>
            <ng-container *ngIf="apiState[i] && !apiState[i]?.loading && !apiState[i]?.error">
              <div class="dvm-popup-api-empty" *ngIf="!(apiState[i]?.groups || []).length">No data available.</div>
              <div class="dvm-popup-api-group" *ngFor="let group of apiState[i]?.groups || []; trackBy: trackByIndex">
                <button
                  type="button"
                  class="dvm-popup-api-group-head"
                  *ngIf="group.meta.length"
                  (click)="group.expanded = !group.expanded"
                >
                  <span class="dvm-popup-api-meta" *ngFor="let meta of group.meta; trackBy: trackByIndex">
                    {{ meta.label }}: <strong>{{ meta.value }}</strong>
                  </span>
                  <span class="dvm-popup-api-count">Changes ({{ group.rows.length }})</span>
                  <mat-icon>{{ group.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
                <div class="dvm-popup-table-wrap" *ngIf="group.expanded || !group.meta.length">
                  <table class="dvm-popup-table" *ngIf="group.rows.length; else emptyApiRows">
                    <thead>
                      <tr>
                        <th *ngFor="let col of apiTableColumns(block, group); trackBy: trackByIndex">{{ col.label || col.field }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of group.rows; trackBy: trackByIndex">
                        <td
                          *ngFor="let col of apiTableColumns(block, group); trackBy: trackByIndex"
                          [style.color]="colorFor(col.color)"
                        >{{ formatCell(row[col.field]) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <ng-template #emptyApiRows>
                    <div class="dvm-popup-table-empty">No data available.</div>
                  </ng-template>
                </div>
              </div>
              <div class="dvm-popup-api-legend" *ngIf="legendColumns(block).length">
                <span class="dvm-popup-api-legend-item" *ngFor="let col of legendColumns(block); trackBy: trackByIndex">
                  <i class="dvm-popup-api-dot" [style.background]="colorFor(col.color)"></i>{{ col.label || col.field }}
                </span>
              </div>
            </ng-container>
          </div>

          <!-- Action buttons. -->
          <div *ngSwitchCase="'buttons'" class="dvm-popup-block dvm-popup-buttons" [style.justify-content]="block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'">
            <button
              *ngFor="let btn of block.buttons || []; trackBy: trackByIndex"
              type="button"
              class="dvm-popup-action"
              [class.is-primary]="(btn.style || 'primary') === 'primary'"
              [class.is-secondary]="btn.style === 'secondary'"
              [class.is-link]="btn.style === 'link'"
              (click)="onButtonClick(btn)"
            >{{ btn.label }}</button>
          </div>

          <!-- Divider -->
          <hr *ngSwitchCase="'divider'" class="dvm-popup-block dvm-popup-divider" />
        </ng-container>
      </ng-container>

      <div *ngIf="!blocks.length" class="dvm-popup-empty">No content configured for this popup yet.</div>
    </div>

    <footer class="dvm-popup-footer" *ngIf="closeLabel">
      <button type="button" class="dvm-popup-action is-secondary" (click)="closePopup()">{{ closeLabel }}</button>
    </footer>
  </div>
</div>
